import Bluebird from 'bluebird';
import { getEpochTime } from '../Date';
import { AnchorAPI } from '.';
import { Podcast, PodcastBase } from '../../types';

const getSponsorshipsActivationStatus = (
  doHideDefaultAdSlotsInEpisodes: boolean,
  hasEverEnabledSponsorships: boolean
) => {
  if (
    !hasEverEnabledSponsorships &&
    doHideDefaultAdSlotsInEpisodes === undefined
  ) {
    return 'notYetActivated';
  }
  if (
    hasEverEnabledSponsorships &&
    doHideDefaultAdSlotsInEpisodes === undefined
  ) {
    return 'activated';
  }
  if (hasEverEnabledSponsorships && doHideDefaultAdSlotsInEpisodes) {
    return 'deactivated';
  }
  if (hasEverEnabledSponsorships && !doHideDefaultAdSlotsInEpisodes) {
    return 'activated';
  }
  if (!hasEverEnabledSponsorships && doHideDefaultAdSlotsInEpisodes) {
    return 'deactivated';
  }
  if (!hasEverEnabledSponsorships && !doHideDefaultAdSlotsInEpisodes) {
    return 'activated';
  }
  return 'notYetActivated';
};

const fetchAndConstructPodcastObject = () =>
  new Promise<any>((resolve, reject) => {
    let podcast: any = {};
    return AnchorAPI.fetchPodcastEpisodes()
      .then((podcastEpisodesJSON: any) => {
        podcast = {
          ...podcastEpisodesJSON,
          podcastEpisodes: [],
          episodesById: {},
          podcastEpisodeIds: podcastEpisodesJSON.allEpisodeWebIds,
        };
        return AnchorAPI.fetchMoney().then((responseJSON: any) => {
          podcast.money = responseJSON;
          return AnchorAPI.fetchPodcastStatus()
            .then((podcastStatusJSON: any) => {
              podcast.status = podcastStatusJSON;
              return AnchorAPI.fetchMyPodcastMetadata()
                .then((podcastMetadataJSON: any) => {
                  const { webStationId } = podcastMetadataJSON;
                  podcast = {
                    ...podcast,
                    metadata: podcastMetadataJSON,
                    webStationId,
                    stationId: webStationId,
                  };
                  // TODO: migrate to webStationId
                  return AnchorAPI.fetchPodcastProfileForStation(webStationId)
                    .then((profileJSON: any) => {
                      podcast.profile = profileJSON;
                      return AnchorAPI.fetchAdCampaigns(webStationId)
                        .then((responseJSON: any) => {
                          const { adCampaigns, audios } = responseJSON;
                          const campaigns = adCampaigns.map(
                            (adCampaignJSON: any) => {
                              const {
                                audioId: campaignAudioId,
                                exampleAudioId: campaignExampleAudioId,
                              } = adCampaignJSON;
                              return {
                                ...adCampaignJSON,
                                audio: audios.find((audioJSON: any) => {
                                  const { audioId } = audioJSON;
                                  return audioId === campaignAudioId;
                                }),
                                exampleAudio: audios.find((audioJSON: any) => {
                                  const { audioId } = audioJSON;
                                  return audioId === campaignExampleAudioId;
                                }),
                              };
                            }
                          );
                          podcast.campaigns = campaigns;
                          resolve(podcast);
                        })
                        .catch((error: Error) => {
                          console.error(`fetchAdCampaigns error: ${error}`);
                        });
                    })
                    .catch((error: Error) => {
                      console.error(
                        `fetchPodcastProfileForStation error: ${error}`
                      );
                    });
                })
                .catch((error: Error) => {
                  console.error(`fetchMyPodcastMetadata error: ${error}`);
                });
            })
            .catch((error: Error) => {
              console.error(`fetchPodcastStatus error: ${error}`);
            });
        });
      })
      .catch((error: Error) => {
        console.error(`fetchPodcastEpisodes error: ${error}`);
      });
  });

const decodePodcastObjectIntoPodcast = (podcast: any): Podcast => {
  // A lot of things do not work if a podcast doesn't have a vanity slug: public profile, sharing links
  //   We either need to figure out a way to enforce every podcast has a vanity slug or
  //   work with product to determine what we do when a vanity slugs doesn't exist
  if (!podcast.metadata.vanitySlug) {
    setTimeout(() => {
      throw new Error(
        `Podcast (webId: ${podcast.podcastId}) doesnt have a vanitySlug`
      );
    }, 300);
  }
  const podcastBaseType: PodcastBase = {
    id: { kind: 'webId', webId: podcast.podcastId },
    createdEpochTime: getEpochTime(new Date(podcast.stationCreatedDate)),
    hasAcceptedTOSMonetization: Boolean(
      podcast.metadata.hasAcceptedTOSMonetization
    ),
    hasSeenSponsorshipsReminderModal:
      podcast.metadata.hasSeenSponsorshipsReminderModal,
    isAutoPairable: podcast.status.isAutoPairable,
    feedUrl: podcast.metadata.feedUrl,
    hasAnchorBranding: podcast.metadata.hasAnchorBranding,
    isExplicit: Boolean(podcast.metadata.isExplicit),
    iTunesCategory: podcast.metadata.itunesCategory,
    name: podcast.metadata.podcastName,
    vanitySlug: podcast.metadata.vanitySlug,
    description: podcast.metadata.podcastDescription,
    safePodcastDescription: podcast.metadata.safePodcastDescription,
    coverartImage:
      podcast.metadata.podcastImage || podcast.metadata.podcastImageFull
        ? {
            normalSize: podcast.metadata.podcastImage
              ? {
                  url: podcast.metadata.podcastImage,
                }
              : undefined,
            fullSize: podcast.metadata.podcastImageFull
              ? {
                  url: podcast.metadata.podcastImageFull,
                }
              : undefined,
          }
        : undefined,
    listeningPlatforms: [
      ...Object.keys(podcast.profile.podcastUrlDictionary).map(
        podcastUrlDictionaryKey => ({
          name: podcastUrlDictionaryKey,
          url: podcast.profile.podcastUrlDictionary[podcastUrlDictionaryKey],
        })
      ),
      {
        name: 'anchor',
        url: '????',
      },
    ],
    episodeIds: podcast.podcastEpisodeIds.map((webId: string) => ({
      kind: 'webId',
      webId,
    })),
    hasAuthenticatedStripe: Boolean(podcast.money.hasAuthenticatedStripe),
    // TODO: Confirm this logic.... seems like a lot just to determine is sponsorships is enabled...
    sponsorshipsActivationStatus: getSponsorshipsActivationStatus(
      podcast.metadata.doHideDefaultAdSlotsInEpisodes,
      podcast.metadata.hasEverEnabledSponsorships
    ),
    hasSupportersEnabled: Boolean(podcast.money.hasSupportersEnabled),
    doEnableListenerSupportLinkInEpisodes: Boolean(
      podcast.metadata.doEnableListenerSupportLinkInEpisodes
    ),
    podcastLanguage: podcast.profile.podcastMetadata.podcastLanguage,
    sponsorshipCampaignsId: podcast.campaigns.map((campaign: any) => ({
      kind: 'id',
      id: campaign.id,
    })),
    distributionRequestStatus: podcast.status.podcastStatus
      ? podcast.status.podcastStatus
      : 'noActionTaken',
    playDataRemoteData: { kind: 'notAsked' },
    walletRemoteData: { kind: 'notAsked' },
    // totalPlaysAllTime: ?????
    // availableBalanceInCents: number;
    // centsPending: number;
  };
  const {
    podcastExternalSource,
    podcastExternalSourceRedirected,
    podcastExternalSourceExpired,
  } = podcast.metadata;
  const externalSource: string =
    podcastExternalSource ||
    podcastExternalSourceRedirected ||
    podcastExternalSourceExpired;
  const podcastType: Podcast = externalSource
    ? {
        kind: 'importedPodcast',
        redirectStatus: podcastExternalSourceRedirected
          ? 'redirected'
          : podcastExternalSourceExpired
          ? 'ignored'
          : 'pending',
        externalSource,
        ...podcastBaseType,
      }
    : {
        kind: 'nonImportedPodcast',
        ...podcastBaseType,
      };
  return podcastType;
};

const fetchCurrentUsersPodcast = () =>
  fetchAndConstructPodcastObject().then(decodePodcastObjectIntoPodcast);

export { fetchCurrentUsersPodcast, fetchAndConstructPodcastObject };
